import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { Download, Loader2, Check, AlertTriangle, Edit2, CalendarDays } from "lucide-react";
import { apiClient } from "@/App";
import { toast } from "sonner";
import { tokens } from "@/design-system/tokens";

// Inline button component
const Button = ({ onClick, disabled = false, children, variant = "primary", size = "sm", className = "", ...props }) => {
  const isPrimary = variant === "primary";
  const isGhost = variant === "ghost";

  const baseStyle = {
    padding: size === "sm" ? "6px 12px" : "8px 16px",
    fontSize: "12px",
    fontWeight: 500,
    borderRadius: tokens.radius.md,
    border: "none",
    cursor: disabled ? "not-allowed" : "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    transition: "all 0.2s",
    opacity: disabled ? 0.6 : 1,
  };

  const primaryStyle = {
    ...baseStyle,
    backgroundColor: tokens.color.orange,
    color: tokens.color.text,
  };

  const ghostStyle = {
    ...baseStyle,
    backgroundColor: "transparent",
    color: tokens.color.textMuted,
  };

  const style = isPrimary ? primaryStyle : ghostStyle;

  return (
    <button onClick={onClick} disabled={disabled} style={style} {...props}>
      {children}
    </button>
  );
};

// Inline label component
const Label = ({ htmlFor, children, className = "", ...props }) => (
  <label htmlFor={htmlFor} style={{ fontSize: "10px", color: tokens.color.textMuted, cursor: "pointer" }} {...props}>
    {children}
  </label>
);

// Inline badge component
const Badge = ({ variant = "secondary", children, className = "" }) => {
  const isOutline = variant === "outline";
  const style = isOutline
    ? { fontSize: "10px", padding: "2px 6px", backgroundColor: tokens.color.amberSoft, color: tokens.color.amber, borderRadius: "4px", border: "1px solid rgba(202, 138, 4, 0.5)", display: "inline-flex", alignItems: "center", gap: "4px" }
    : { fontSize: "10px", padding: "2px 6px", backgroundColor: tokens.color.innerBg, color: tokens.color.textMuted, borderRadius: "4px", display: "inline-flex", alignItems: "center", gap: "4px" };
  return <span style={style}>{children}</span>;
};

// Inline input component
const Input = ({ value, onChange, placeholder = "", className = "" }) => (
  <input
    type="text"
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    style={{
      height: "28px",
      fontSize: "12px",
      padding: "4px 8px",
      backgroundColor: tokens.color.innerBg,
      border: `1px solid ${tokens.color.innerBorder}`,
      borderRadius: "4px",
      color: tokens.color.text,
      width: "100%",
    }}
  />
);

export const CalendarImport = ({ onComplete }) => {
  // step: idle | calendar-select | loading | preview | done
  const [step, setStep] = useState("idle");
  const [includePast, setIncludePast] = useState(false);

  // Calendar selector state
  const [calendars, setCalendars] = useState([]);
  const [selectedCalendarIds, setSelectedCalendarIds] = useState(new Set());
  const [loadingCalendars, setLoadingCalendars] = useState(false);

  // Event preview state
  const [events, setEvents] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [totalEvents, setTotalEvents] = useState(0);
  const [importing, setImporting] = useState(false);
  const [editIdx, setEditIdx] = useState(null);

  // ── Step 1: fetch calendar list ──────────────────────────────────────────
  const handleOpenCalendarSelect = async () => {
    setLoadingCalendars(true);
    try {
      const res = await apiClient.get("/calendar/list");
      const cals = res.data.calendars || [];
      setCalendars(cals);
      // Pre-select primary calendar
      const initial = new Set(cals.filter(c => c.primary).map(c => c.id));
      if (initial.size === 0 && cals.length > 0) initial.add(cals[0].id);
      setSelectedCalendarIds(initial);
      setStep("calendar-select");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fetch calendar list");
    } finally {
      setLoadingCalendars(false);
    }
  };

  const toggleCalendar = (id) => {
    setSelectedCalendarIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // ── Step 2: fetch event preview ──────────────────────────────────────────
  const handlePreview = async () => {
    if (selectedCalendarIds.size === 0) {
      toast.error("Select at least one calendar");
      return;
    }
    setStep("loading");
    try {
      const res = await apiClient.post(
        `/calendar/import/preview?include_past=${includePast}`,
        { calendar_ids: Array.from(selectedCalendarIds), include_past: includePast }
      );
      const gigs = (res.data.potential_gigs || []).map(g => ({
        ...g,
        is_public: true, // default — user can toggle per event
      }));
      setEvents(gigs);
      setTotalEvents(res.data.total_events || 0);
      // Pre-select non-duplicates
      const ids = new Set();
      gigs.forEach((g, i) => { if (!g.is_duplicate) ids.add(i); });
      setSelectedIds(ids);
      setStep("preview");
      if (gigs.length === 0) toast.info("No potential gigs found in the selected calendars.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to read calendar");
      setStep("calendar-select");
    }
  };

  // ── Preview helpers ──────────────────────────────────────────────────────
  const toggleSelect = (idx) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(events.map((_, i) => i)));
  const deselectAll = () => setSelectedIds(new Set());

  const updateEvent = (idx, field, value) => {
    setEvents(prev => prev.map((e, i) => i === idx ? { ...e, [field]: value } : e));
  };

  // ── Step 3: confirm import ───────────────────────────────────────────────
  const handleImport = async () => {
    const toImport = events.filter((_, i) => selectedIds.has(i));
    if (toImport.length === 0) {
      toast.error("Select at least one event to import");
      return;
    }
    setImporting(true);
    try {
      const res = await apiClient.post("/calendar/import/confirm", { gigs: toImport });
      toast.success(`Imported ${res.data.imported} gig(s)!`);
      setStep("done");
      if (onComplete) onComplete(res.data.imported);
    } catch (error) {
      toast.error("Failed to import gigs");
    } finally {
      setImporting(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  if (step === "idle") {
    // No heading of its own — this renders inside Settings' "Import Google
    // Calendar gigs" IntegrationRow, whose title/description used to be
    // near-duplicated here (and the duplicate text squeezed the button on
    // mobile). Just the controls: past-events toggle left, scan button
    // right, wrapping onto two lines instead of squashing when narrow.
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: `${tokens.space.md}px`,
        }}
        data-testid="calendar-import-idle"
      >
        <div style={{ display: "flex", alignItems: "center", gap: `${tokens.space.sm}px` }}>
          <Switch id="include-past" checked={includePast} onCheckedChange={setIncludePast} />
          <Label htmlFor="include-past">Include past events</Label>
        </div>
        <Button onClick={handleOpenCalendarSelect} size="sm" disabled={loadingCalendars} data-testid="calendar-import-scan-btn">
          {loadingCalendars
            ? <Loader2 style={{ width: "16px", height: "16px", animation: "spin 1s linear infinite" }} />
            : <Download style={{ width: "16px", height: "16px" }} />}
          Scan Calendar
        </Button>
      </div>
    );
  }

  if (step === "calendar-select") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: `${tokens.space.lg}px` }} data-testid="calendar-import-calendar-select">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <h4 style={{ fontWeight: 500, fontSize: "12px", color: tokens.color.text }}>Choose calendars to scan</h4>
            <p style={{ fontSize: "10px", color: tokens.color.textMuted }}>{calendars.length} calendar{calendars.length !== 1 ? "s" : ""} found</p>
            <p style={{ fontSize: "11px", color: tokens.color.textMuted, marginTop: `${tokens.space.xs}px`, maxWidth: "36ch" }}>
              Tick only the calendar you keep your gigs in. Personal events, like family plans, will be listed as gigs if you tick them.
            </p>
          </div>
          <div style={{ display: "flex", gap: `${tokens.space.sm}px` }}>
            <Button variant="ghost" size="sm" onClick={() => setStep("idle")}>Back</Button>
            <Button
              size="sm"
              onClick={handlePreview}
              disabled={selectedCalendarIds.size === 0}
              data-testid="calendar-import-scan-btn"
            >
              <CalendarDays style={{ width: "16px", height: "16px" }} />
              Scan {selectedCalendarIds.size > 0 ? `(${selectedCalendarIds.size})` : ""}
            </Button>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: `${tokens.space.sm}px` }}>
          {calendars.map(cal => (
            <label
              key={cal.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: `${tokens.space.md}px`,
                padding: `${tokens.space.sm}px`,
                borderRadius: tokens.radius.md,
                cursor: "pointer",
                backgroundColor: "rgba(39, 39, 42, 0.3)",
                transition: "background-color 0.2s"
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(39, 39, 42, 0.6)")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "rgba(39, 39, 42, 0.3)")}
            >
              <Checkbox
                checked={selectedCalendarIds.has(cal.id)}
                onCheckedChange={() => toggleCalendar(cal.id)}
              />
              <span
                style={{
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  flexShrink: 0,
                  backgroundColor: cal.backgroundColor
                }}
              />
              <span style={{ fontSize: "12px", flex: 1, overflow: "hidden", textOverflow: "ellipsis", color: tokens.color.text }}>{cal.summary}</span>
              {cal.primary && (
                <Badge variant="secondary">Primary</Badge>
              )}
            </label>
          ))}
        </div>
      </div>
    );
  }

  if (step === "loading") {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: `${tokens.space.md}px`, paddingTop: "24px", paddingBottom: "24px", justifyContent: "center" }} data-testid="calendar-import-loading">
        <Loader2 style={{ width: "20px", height: "20px", animation: "spin 1s linear infinite", color: tokens.color.orange }} />
        <span style={{ fontSize: "12px", color: tokens.color.textMuted }}>Reading your calendar{selectedCalendarIds.size > 1 ? "s" : ""}…</span>
      </div>
    );
  }

  if (step === "done") {
    return (
      <div style={{ textAlign: "center", paddingTop: "16px", paddingBottom: "16px" }} data-testid="calendar-import-done">
        <Check style={{ width: "32px", height: "32px", color: tokens.color.green, margin: "0 auto 8px" }} />
        <p style={{ fontSize: "12px", fontWeight: 500, color: tokens.color.text }}>Import complete!</p>
        <Button variant="ghost" size="sm" onClick={() => setStep("idle")} style={{ marginTop: `${tokens.space.sm}px` }}>Import more</Button>
      </div>
    );
  }

  // ── Preview step ─────────────────────────────────────────────────────────
  const allSelected = events.length > 0 && selectedIds.size === events.length;
  const noneSelected = selectedIds.size === 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: `${tokens.space.md}px` }} data-testid="calendar-import-preview">
      {/* Header row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <p style={{ fontSize: "12px", fontWeight: 500, color: tokens.color.text }}>
          Found {events.length} potential gig{events.length !== 1 ? "s" : ""}
          <span style={{ color: tokens.color.textMuted, fontWeight: "normal" }}> from {totalEvents} events</span>
        </p>
        <div style={{ display: "flex", gap: `${tokens.space.sm}px` }}>
          <Button variant="ghost" size="sm" onClick={() => setStep("calendar-select")}>Back</Button>
          <Button
            size="sm"
            onClick={handleImport}
            disabled={importing || selectedIds.size === 0}
            data-testid="calendar-import-confirm-btn"
          >
            {importing ? <Loader2 style={{ width: "16px", height: "16px", animation: "spin 1s linear infinite" }} /> : <Check style={{ width: "16px", height: "16px" }} />}
            Import {selectedIds.size > 0 ? selectedIds.size : ""}
          </Button>
        </div>
      </div>

      {/* Select / deselect all */}
      <div style={{ display: "flex", alignItems: "center", gap: `${tokens.space.md}px`, paddingBottom: "4px", borderBottom: `1px solid ${tokens.color.innerBorder}` }}>
        <button
          onClick={allSelected ? deselectAll : selectAll}
          style={{ fontSize: "10px", color: tokens.color.orange, fontWeight: 500, background: "none", border: "none", cursor: "pointer", padding: 0 }}
        >
          {allSelected ? "Deselect all" : "Select all"}
        </button>
        {!noneSelected && !allSelected && (
          <span style={{ fontSize: "10px", color: tokens.color.textMuted }}>{selectedIds.size} selected</span>
        )}
        <button onClick={deselectAll} style={{ fontSize: "10px", color: tokens.color.textMuted, background: "none", border: "none", cursor: "pointer", padding: 0, marginLeft: "auto" }}>
          Clear
        </button>
      </div>

      {/* Scrollable event list */}
      <ScrollArea style={{ height: "420px", paddingRight: "4px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: `${tokens.space.sm}px` }}>
          {events.map((evt, idx) => (
            <div
              key={idx}
              style={{
                padding: `${tokens.space.md}px`,
                borderRadius: tokens.radius.md,
                border: `1px solid ${
                  selectedIds.has(idx) ? "rgba(245, 137, 31, 0.4)" : tokens.color.innerBorder
                }${evt.is_duplicate ? " rgba(202, 138, 4, 0.5)" : ""}`,
                backgroundColor: selectedIds.has(idx) ? "rgba(245, 137, 31, 0.1)" : tokens.color.innerBg,
                transition: "all 0.2s",
              }}
            >
              <div style={{ display: "flex", alignItems: "flex-start", gap: `${tokens.space.md}px` }}>
                <Checkbox
                  checked={selectedIds.has(idx)}
                  onCheckedChange={() => toggleSelect(idx)}
                  style={{ marginTop: "4px", flexShrink: 0 }}
                />
                <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: `${tokens.space.xs}px` }}>
                  {editIdx === idx ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: `${tokens.space.sm}px` }}>
                      <Input
                        value={evt.artist_name}
                        onChange={(e) => updateEvent(idx, "artist_name", e.target.value)}
                        placeholder="Artist / title"
                      />
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: `${tokens.space.sm}px` }}>
                        <Input
                          value={evt.venue_name}
                          onChange={(e) => updateEvent(idx, "venue_name", e.target.value)}
                          placeholder="Venue"
                        />
                        <Input
                          value={evt.city}
                          onChange={(e) => updateEvent(idx, "city", e.target.value)}
                          placeholder="City"
                        />
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setEditIdx(null)} style={{ height: "24px", fontSize: "10px" }}>Done</Button>
                    </div>
                  ) : (
                    <>
                      {/* Title row */}
                      <div style={{ display: "flex", alignItems: "center", gap: `${tokens.space.sm}px` }}>
                        <p style={{ fontWeight: 500, fontSize: "12px", overflow: "hidden", textOverflow: "ellipsis", flex: 1, color: tokens.color.text }}>
                          {evt.artist_name || evt.title || "Untitled"}
                        </p>
                        <button
                          onClick={() => setEditIdx(idx)}
                          style={{ color: tokens.color.textMuted, background: "none", border: "none", cursor: "pointer", padding: 0, flexShrink: 0 }}
                          title="Edit"
                        >
                          <Edit2 style={{ width: "12px", height: "12px" }} />
                        </button>
                      </div>

                      {/* Date / time / location */}
                      <p style={{ fontSize: "10px", color: tokens.color.textMuted }}>
                        {evt.date
                          ? new Date(evt.date + "T12:00:00").toLocaleDateString("en-GB", {
                              weekday: "short", day: "numeric", month: "short", year: "numeric",
                            })
                          : "No date"}
                        {evt.start_time ? ` · ${evt.start_time}` : ""}
                        {evt.end_time ? `–${evt.end_time}` : ""}
                      </p>
                      {evt.location && (
                        <p style={{ fontSize: "10px", color: tokens.color.textMuted, overflow: "hidden", textOverflow: "ellipsis" }}>📍 {evt.location}</p>
                      )}
                    </>
                  )}

                  {/* Duplicate warning */}
                  {evt.is_duplicate && (
                    <Badge variant="outline">
                      <AlertTriangle style={{ width: "10px", height: "10px" }} />
                      Possible duplicate
                    </Badge>
                  )}

                  {/* Public / Private toggle */}
                  <div style={{ display: "flex", alignItems: "center", gap: `${tokens.space.sm}px`, paddingTop: `${tokens.space.xs}px` }}>
                    <Switch
                      id={`public-toggle-${idx}`}
                      checked={evt.is_public}
                      onCheckedChange={(val) => updateEvent(idx, "is_public", val)}
                      style={{ transform: "scale(0.75)", transformOrigin: "left center" }}
                    />
                    <Label htmlFor={`public-toggle-${idx}`} style={{ fontSize: "11px" }}>
                      {evt.is_public ? "Public" : "Private"}
                    </Label>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};
